import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Save, Loader2, Palette, Receipt, CreditCard, Settings2, Upload, X, Building2, Image as ImageIcon, FileText, Plus } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import BrandIdentityManager from "./BrandIdentityManager";

const InvoiceSettingsTab = ({ isDark, headers }) => {
  const [settings, setSettings] = useState(null);
  const [business, setBusiness] = useState({
    business_name: "", business_address: "", business_phone: "", business_email: "",
  });
  const [logoPreview, setLogoPreview] = useState(null); // displayed thumbnail
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storeLocations, setStoreLocations] = useState([]);
  const [pdfInfoLoc, setPdfInfoLoc] = useState(""); // "" = all locations (global)
  const fileInputRef = useRef(null);

  // Current scope for the "Additional Information on PDF" section
  const pdfExtras = pdfInfoLoc
    ? (settings?.location_overrides?.[pdfInfoLoc] || { custom_fields: [], default_notes: "" })
    : { custom_fields: settings?.custom_fields || [], default_notes: settings?.default_notes || "" };
  const setPdfExtras = (patch) => {
    if (!pdfInfoLoc) {
      setSettings((s) => ({ ...s, ...patch }));
    } else {
      setSettings((s) => ({
        ...s,
        location_overrides: {
          ...(s.location_overrides || {}),
          [pdfInfoLoc]: { custom_fields: [], default_notes: "", ...(s.location_overrides?.[pdfInfoLoc] || {}), ...patch },
        },
      }));
    }
  };

  useEffect(() => { fetchAll(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tpl, biz] = await Promise.all([
        axios.get(`${API}/invoices/template/settings`, { headers }),
        axios.get(`${API}/settings/business`, { headers }).catch(() => ({ data: {} })),
      ]);
      axios.get(`${API}/store/locations`, { headers })
        .then((r) => setStoreLocations(r.data?.locations || []))
        .catch(() => setStoreLocations([]));
      setSettings(tpl.data || {});
      setBusiness({
        business_name: biz.data?.business_name || "",
        business_address: biz.data?.business_address || "",
        business_phone: biz.data?.business_phone || "",
        business_email: biz.data?.business_email || "",
      });
      // Pre-resolve the logo source. Uploaded file → use the served URL
      // (with cache-buster); else fall back to legacy text URL; else null.
      if (biz.data?.business_logo_file && biz.data?.user_id) {
        setLogoPreview(`${API}/invoices/template/business-logo/${biz.data.user_id}?v=${Date.now()}`);
      } else if (biz.data?.logo_url || tpl.data?.logo_url) {
        setLogoPreview(biz.data?.logo_url || tpl.data?.logo_url);
      } else {
        setLogoPreview(null);
      }
    } catch {
      toast.error("Failed to load settings");
    } finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Send both template settings AND business-identity fields in one call.
      // The backend splits them and updates the correct collection.
      await axios.put(
        `${API}/invoices/template/settings`,
        { ...settings, ...business },
        { headers },
      );
      toast.success("Invoice settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be 5MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/invoices/template/business-logo`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      // Use the URL the server returned (already has a cache-bust).
      setLogoPreview(`${API}${res.data.logo_url}`);
      // Reflect the cleared legacy text URL in local state too.
      setSettings((s) => ({ ...s, logo_url: "" }));
      toast.success("Logo uploaded — it'll appear on new invoices and previews");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the uploaded logo?")) return;
    try {
      await axios.delete(`${API}/invoices/template/business-logo`, { headers });
      setLogoPreview(null);
      setSettings((s) => ({ ...s, logo_url: "" }));
      toast.success("Logo removed");
    } catch {
      toast.error("Failed to remove logo");
    }
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const update = (field, value) => setSettings(s => ({ ...s, [field]: value }));
  const updateBiz = (field, value) => setBusiness(b => ({ ...b, [field]: value }));

  return (
    <div className="space-y-5" data-testid="invoice-settings-tab">
      {/* Branding Section */}
      <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-blue-500" />
            <CardTitle className="text-base">Branding</CardTitle>
          </div>
          <CardDescription>Colors and logo for your invoices and estimates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Primary Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={settings.primary_color || "#0046BE"} onChange={(e) => update("primary_color", e.target.value)}
                  className="w-9 h-9 rounded-lg cursor-pointer border-0" data-testid="settings-primary-color" />
                <Input value={settings.primary_color || "#0046BE"} onChange={(e) => update("primary_color", e.target.value)}
                  className="h-9 text-sm font-mono" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Accent Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={settings.accent_color || "#FFCC00"} onChange={(e) => update("accent_color", e.target.value)}
                  className="w-9 h-9 rounded-lg cursor-pointer border-0" data-testid="settings-accent-color" />
                <Input value={settings.accent_color || "#FFCC00"} onChange={(e) => update("accent_color", e.target.value)}
                  className="h-9 text-sm font-mono" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Logo</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="settings-logo-file-input"
            />
            {logoPreview ? (
              <div className="flex items-center gap-3" data-testid="settings-logo-preview">
                <div className={`rounded-lg border ${isDark ? "border-zinc-700 bg-zinc-800" : "border-gray-200 bg-white"} p-2 flex items-center justify-center w-24 h-16 overflow-hidden`}>
                  <img src={logoPreview} alt="Business logo" className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="settings-logo-replace-btn"
                  >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                    Replace
                  </Button>
                  <Button
                    type="button" size="sm" variant="ghost"
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10 h-8"
                    onClick={removeLogo}
                    data-testid="settings-logo-remove-btn"
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={`w-full flex flex-col items-center justify-center gap-2 py-6 rounded-lg border-2 border-dashed transition-colors ${
                  isDark ? "border-zinc-700 hover:border-blue-500/50 bg-zinc-800/30" : "border-gray-300 hover:border-blue-500/50 bg-gray-50"
                }`}
                data-testid="settings-logo-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Uploading…</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-6 h-6 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Upload your logo</span>
                    <span className="text-[11px] text-muted-foreground">PNG, JPG, WebP or SVG · 5MB max</span>
                  </>
                )}
              </button>
            )}
            <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
              Appears on every PDF, invoice preview, and customer payment page.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Show Logo on Documents</Label>
            <Switch checked={settings.show_logo !== false} onCheckedChange={(v) => update("show_logo", v)} data-testid="settings-show-logo" />
          </div>
        </CardContent>
      </Card>

      {/* Business Identity Section */}
      <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-amber-500" />
            <CardTitle className="text-base">Business Identity</CardTitle>
          </div>
          <CardDescription>How your business appears on invoices, estimates, and the customer payment page.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Business Name</Label>
              <Input value={business.business_name} onChange={(e) => updateBiz("business_name", e.target.value)}
                placeholder="Acme Locksmith LLC" className="h-9 text-sm" data-testid="settings-business-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Phone</Label>
              <Input value={business.business_phone} onChange={(e) => updateBiz("business_phone", e.target.value)}
                placeholder="(555) 123-4567" className="h-9 text-sm" data-testid="settings-business-phone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input type="email" value={business.business_email} onChange={(e) => updateBiz("business_email", e.target.value)}
              placeholder="billing@yourcompany.com" className="h-9 text-sm" data-testid="settings-business-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Address (shown on invoices & estimates)</Label>
            <Textarea
              value={business.business_address}
              onChange={(e) => updateBiz("business_address", e.target.value)}
              placeholder={"123 Main Street\nSuite 200\nAustin, TX 78701"}
              className="text-sm min-h-[72px]"
              rows={3}
              data-testid="settings-business-address"
            />
            <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
              Multi-line OK — line breaks are preserved on the PDF and public view.
            </p>
          </div>

          {/* Multi-brand manager — renders only for multi-location accounts */}
          <BrandIdentityManager isDark={isDark} headers={headers} />
        </CardContent>
      </Card>

      {/* Taxes Section */}
      <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-green-500" />
            <CardTitle className="text-base">Taxes</CardTitle>
          </div>
          <CardDescription>Default tax rate applied to new invoices and estimates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Default Tax Rate (%)</Label>
            <Input type="number" step="0.1" min="0" max="100"
              value={settings.default_tax_rate || 0}
              onChange={(e) => update("default_tax_rate", parseFloat(e.target.value) || 0)}
              className="h-9 text-sm w-32" data-testid="settings-default-tax" />
            <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
              Automatically applied when creating new invoices. Can be changed per-document.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Fees Section */}
      <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-purple-500" />
            <CardTitle className="text-base">Credit Fee</CardTitle>
          </div>
          <CardDescription>Default credit/processing fee for invoices and estimates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Enable Credit Fee by Default</Label>
            <Switch checked={settings.default_credit_fee_enabled || false}
              onCheckedChange={(v) => update("default_credit_fee_enabled", v)}
              data-testid="settings-fee-enabled" />
          </div>

          {settings.default_credit_fee_enabled && (
            <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Fee Type</Label>
                  <Select value={settings.default_credit_fee_type || "percentage"}
                    onValueChange={(v) => update("default_credit_fee_type", v)}>
                    <SelectTrigger className="h-9" data-testid="settings-fee-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="flat">Flat Amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Default Value {settings.default_credit_fee_type === "percentage" ? "(%)" : "($)"}
                  </Label>
                  <Input type="number" step="0.01" min="0"
                    value={settings.default_credit_fee_value || 0}
                    onChange={(e) => update("default_credit_fee_value", parseFloat(e.target.value) || 0)}
                    className="h-9 text-sm" data-testid="settings-fee-value" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Allow Per-Invoice Override</Label>
                  <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                    Let users change the fee on individual invoices
                  </p>
                </div>
                <Switch checked={settings.allow_invoice_fee_override !== false}
                  onCheckedChange={(v) => update("allow_invoice_fee_override", v)}
                  data-testid="settings-fee-override" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Defaults Section */}
      <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-orange-500" />
            <CardTitle className="text-base">Defaults</CardTitle>
          </div>
          <CardDescription>Template and display defaults</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Template Style</Label>
            <Select value={settings.template || "modern"} onValueChange={(v) => update("template", v)}>
              <SelectTrigger className="h-9 w-48" data-testid="settings-template"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="modern">Modern</SelectItem>
                <SelectItem value="classic">Classic</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customer Payment Options */}
      <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-emerald-500" />
            <CardTitle className="text-base">Customer Payment Options</CardTitle>
          </div>
          <CardDescription>What customers can pay on the online payment page (Stripe)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Allow Deposit Payments</Label>
              <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                When an invoice has a "Deposit required" amount, customers can pay just the deposit
              </p>
            </div>
            <Switch
              checked={(settings.customer_payment_options?.deposit) !== false}
              onCheckedChange={(v) => update("customer_payment_options", { ...(settings.customer_payment_options || {}), deposit: v, balance: true })}
              data-testid="settings-pay-deposit"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Allow Custom Amounts</Label>
              <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                Customers can choose how much to pay (up to the remaining balance)
              </p>
            </div>
            <Switch
              checked={(settings.customer_payment_options?.custom) !== false}
              onCheckedChange={(v) => update("customer_payment_options", { ...(settings.customer_payment_options || {}), custom: v, balance: true })}
              data-testid="settings-pay-custom"
            />
          </div>
          <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
            Paying the remaining balance is always available. Payments never exceed the amount still owed.
          </p>
        </CardContent>
      </Card>

      {/* Additional PDF Information & Default Notes */}
      <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-violet-500" />
            <CardTitle className="text-base">Additional Information on PDF</CardTitle>
          </div>
          <CardDescription>Extra lines shown under your business info on every invoice & estimate PDF (e.g. Business License #), plus default notes for new documents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {storeLocations.length >= 2 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Set for</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="pdf-info-location-tabs">
                <button
                  type="button"
                  onClick={() => setPdfInfoLoc("")}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    pdfInfoLoc === ""
                      ? "bg-[#FFCC00]/15 text-[#b8930a] dark:text-[#FFCC00] border-[#FFCC00]/50 font-semibold"
                      : isDark ? "border-white/15 text-white/60 hover:border-white/30" : "border-gray-300 text-gray-500 hover:border-gray-400"
                  }`}
                  data-testid="pdf-info-loc-global"
                >
                  All locations (default)
                </button>
                {storeLocations.map((loc) => (
                  <button
                    key={loc.location_id}
                    type="button"
                    onClick={() => setPdfInfoLoc(loc.location_id)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      pdfInfoLoc === loc.location_id
                        ? "bg-[#FFCC00]/15 text-[#b8930a] dark:text-[#FFCC00] border-[#FFCC00]/50 font-semibold"
                        : isDark ? "border-white/15 text-white/60 hover:border-white/30" : "border-gray-300 text-gray-500 hover:border-gray-400"
                    }`}
                    data-testid={`pdf-info-loc-${loc.location_id}`}
                  >
                    {loc.name}
                  </button>
                ))}
              </div>
              {pdfInfoLoc && (
                <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                  These values apply only to documents assigned to this location. Leave empty to use the "All locations" defaults.
                </p>
              )}
            </div>
          )}
          <div className="space-y-2">
            {(pdfExtras.custom_fields || []).map((cf, i) => (
              <div key={`${pdfInfoLoc}-${i}`} className="flex items-center gap-2" data-testid={`custom-field-row-${i}`}>
                <Input
                  value={cf.label}
                  onChange={(e) => {
                    const next = [...(pdfExtras.custom_fields || [])];
                    next[i] = { ...next[i], label: e.target.value };
                    setPdfExtras({ custom_fields: next });
                  }}
                  placeholder="Label (e.g. Business License)"
                  className="h-9 w-56"
                  maxLength={60}
                  data-testid={`custom-field-label-${i}`}
                />
                <Input
                  value={cf.value}
                  onChange={(e) => {
                    const next = [...(pdfExtras.custom_fields || [])];
                    next[i] = { ...next[i], value: e.target.value };
                    setPdfExtras({ custom_fields: next });
                  }}
                  placeholder="Value (e.g. LIC-123456)"
                  className="h-9 flex-1"
                  maxLength={200}
                  data-testid={`custom-field-value-${i}`}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-red-500"
                  onClick={() => setPdfExtras({ custom_fields: (pdfExtras.custom_fields || []).filter((_, idx) => idx !== i) })}
                  data-testid={`custom-field-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {(pdfExtras.custom_fields || []).length < 10 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setPdfExtras({ custom_fields: [...(pdfExtras.custom_fields || []), { label: "", value: "" }] })}
                data-testid="add-custom-field-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add field
              </Button>
            )}
            <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
              Shown on the PDF as "Label: Value" lines under your business details. Empty rows are ignored.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Default Notes</Label>
            <Textarea
              value={pdfExtras.default_notes || ""}
              onChange={(e) => setPdfExtras({ default_notes: e.target.value })}
              placeholder="e.g. Thank you for your business! All work is guaranteed for 90 days."
              rows={3}
              maxLength={2000}
              data-testid="settings-default-notes"
            />
            <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
              Pre-filled into the Notes field of every new invoice and estimate (you can still edit it per document).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end pt-2 pb-4">
        <Button onClick={saveSettings} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="save-invoice-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};

export default InvoiceSettingsTab;
